import React, { useState, useEffect, useRef, useContext } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Tooltip,
} from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { solarizedlight } from "react-syntax-highlighter/dist/esm/styles/prism";

import { URLS } from "../../../services/urls.ts";
import { TeamContext } from "../../../contexts/team-context.tsx";
import { TeamRouteContext } from "../../team-layout.tsx/index.tsx";
import { getDateString, getScoValue } from "../../../services/utils.ts";
import "./styles.css";
import {
  fetchReport,
  fetchReportMarkdown,
  fetchReportObjects,
  ExtractionObject,
  Report,
  fetchReportSummary,
  fetchReportFileDownloadUrl,
} from "../../../services/stixify_api.ts";
import AddToDossierDialog from "./add-to-dossier-dialog.tsx";
import DeleteFromObjectDialog from "./confirm-delete-from-object.tsx";
import { ExtractionTables } from "./extraction-tables.tsx";

const REACT_APP_PUBLIC_API_BASE_URL = process.env.REACT_APP_PUBLIC_API_BASE_URL;
const REACT_APP_API_SWAGGER_URL = process.env.REACT_APP_API_SWAGGER_URL;
const REACT_APP_TAXII_SWAGGER_URL = process.env.REACT_APP_TAXII_SWAGGER_URL;

const SDO_TYPES = new Set([
  "attack-pattern",
  "campaign",
  "course-of-action",
  "identity",
  "infrastructure",
  "intrusion-set",
  "location",
  "malware",
  "note",
  "report",
  "threat-actor",
  "tool",
  "weakness",
]);

const SCO_TYPES = new Set([
  "domain-name",
  "autonomous-system",
  "bank-account",
  "bank-card",
  "cryptocurrency-transaction",
  "cryptocurrency-wallet",
  "directory",
  "email-addr",
  "file",
  "ipv4-addr",
  "ipv6-addr",
  "mac-addr",
  "network-traffic",
  "phone-number",
  "url",
  "user-agent",
  "windows-registry-key",
]);

const CURL_OBJECTS_COMMAND = `
curl -X 'GET' \\
    '${REACT_APP_PUBLIC_API_BASE_URL}/v1/reports/reportId/objects/' \\
    -H 'accept: application/json' \\
    -H 'API-KEY: XXXX'
`;

const ReportExtractionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { teamId } = useContext(TeamRouteContext);
  const [objects, setObjects] = useState<ExtractionObject[]>([]);
  const [report, setReport] = useState<Report>();
  const [loading, setLoading] = useState<boolean>(true);
  const { activeTeam } = useContext(TeamContext);
  const [reportId, setReportId] = useState("");
  const [markdown, setMarkdown] = useState("");
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [summary, setSummary] = useState("");
  const [openAddToDossierDialog, setOpenAddToDossierDialog] = useState(false);
  const [selectedObject, setSelectedObject] = useState<ExtractionObject>();
  const [
    showConfirmDeleteFromObjectDialog,
    setShowConfirmDeleteFromObjectDialog,
  ] = useState(false);
  const [observables, setObservables] = useState<ExtractionObject[]>([]);
  const [ttps, setTtps] = useState<ExtractionObject[]>([]);
  const [mitreAttacks, setMitreAttacks] = useState<ExtractionObject[]>([]);
  const navigate = useNavigate();

  const stixRef = useRef(null);

  const loadReport = async () => {
    if (!id) return;
    try{
      const response = await fetchReport(teamId, id);
      setReport(response.data);
    } catch(err) {
      if(err?.response?.status == 404) {
        return navigate(URLS.notFound())
      }
      throw err
    }
  };

  const loadSummary = async () => {
    try {
      const response = await fetchReportSummary(teamId || "", id || "");
      setSummary(response.data);
    } catch (err) {
      if (err?.response?.status === 404) {
        setSummary("Report has no summary");
        return;
      }
      throw err;
    }
  };

  const loadMarkdown = async () => {
    const response = await fetchReportMarkdown(teamId || "", id || "");
    setMarkdown(response.data);
  };

  useEffect(() => {
    loadReport();
    loadMarkdown();
    loadSummary();
    loadObjects();
  }, [teamId, id]);

  const fetchAllObjectsApi = async (page: number) => {
    return fetchReportObjects(teamId, id || "", page);
  };
  const fetchAllObjects = async (page: number) => {
    const res = await fetchAllObjectsApi(page);
    const data = res.data;
    const total_results_count = data.total_results_count;
    setObjects((objects) => [...objects, ...data.objects]);
    if (total_results_count / data.page_size > data.page_number) {
      await fetchAllObjects(page + 1);
    }
  };

  const loadObjects = async () => {
    if (!id) return;
    setLoading(true);
    try {
      await fetchAllObjects(1);
    } catch (error) {
      console.error("Failed to fetch jobs:", error);
      // Handle error (e.g., show a notification)
    } finally {
      setLoading(false);
    }
  };

  const getStixObject = () => {
    const reportId = objects.find((object) => object.type === "report")?.id;
    setReportId(reportId || "");
    return {
      type: "bundle",
      id: `bundle--${reportId}`,
      spec_version: "2.1",
      objects,
    };
  };

  const loadStixData = () => {
    if (loading) return;
    if (!scriptLoaded) return;
    if (objects.length === 0) return;

    // eslint-disable-next-line
    const graph = window.stixview.init(
      stixRef.current,
      null,
      () => {
        console.info("Graph loaded");
      },
      {}, // no additional data properties
      {
        hideFooter: false,
        showSidebar: true,
        maxZoom: 50,
        onClickNode: function (node) {},
      }
    );
    const data = getStixObject();
    graph.loadData(data);
  };

  useEffect(loadStixData, [loading, scriptLoaded]);

  const getObservationSearchUrl = (object: ExtractionObject) => {
    return URLS.teamExtractionReports(
      teamId || "",
      object.id,
      object.type,
      getScoValue(object)
    );
  };

  useEffect(() => {
    document.title = `${report?.stixify_file_metadata.name} | Stixify Web`;
  }, [report]);

  const downloadStixObject = async () => {
    const data = getStixObject();
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const reportUUID = reportId.split("--")[1];
    link.download = `bundle--${reportUUID}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  const downloadOriginalFile = async () => {
    if (!report) return;
    const response = await fetchReportFileDownloadUrl(teamId, report.id);
    const url = response.data.url;
    const file_name = response.data.file_name;

    const link = document.createElement("a");
    link.href = url;
    link.download = file_name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  const downloadMarkdown = async () => {
    const blob = new Blob([markdown], { type: "text/md" });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${reportId}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  const mitreAttackCtiButlerConversionDict = {
    "enterprise-attack": "attack-enterprise",
    "ics-attack": "attack-ics",
    "mobile-attack": "attack-mobile",
  };
  const isMitreAttack = (object: ExtractionObject) => {
    const keywords = ["enterprise-attack", "ics-attack", "mobile-attack"];
    if (object.x_mitre_domains) {
      const attackType = object.x_mitre_domains.find((item) =>
        keywords.includes(item)
      );
      if (!attackType) return false;
      object["attackType"] = mitreAttackCtiButlerConversionDict[attackType];
      return true;
    }
    return false;
  };

  const isSDOType = (object: ExtractionObject) => {
    // if (id && object.id?.includes(id)) return false
    if (object.id === "identity--a1f2e3ed-6241-5f05-ac2e-3394213b8e08")
      return false;
    return SDO_TYPES.has(object.type) && !isMitreAttack(object);
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "/stixview.bundle.js";
    script.async = true;
    script.onload = () => {
      setScriptLoaded(true);
    };

    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const filterTTPs = (objects: ExtractionObject[]) => {
    const reportId = `report--${id}`;
    const identityId = report?.stixify_file_metadata.identity.id;
    return objects
      .filter((object) => isSDOType(object) || object.type === "vulnerability")
      .filter((object) => ![reportId, identityId].includes(object.id));
  };

  const filterObjects = (objects: ExtractionObject[]) => {
    const reportId = `report--${id}`;
    const identityId = report?.stixify_file_metadata.identity.id;
    return objects
      .filter((object) => ![reportId, identityId].includes(object.id));
  };

  const removeObject = async (object: ExtractionObject) => {
    setSelectedObject(object);
    setShowConfirmDeleteFromObjectDialog(true);
  };

  const onDeleteFromSelectedObject = () => {
    setObjects((objects) =>
      objects.filter((object) => object.id !== selectedObject?.id)
    );
    setShowConfirmDeleteFromObjectDialog(false);
  };

  useEffect(() => {
    setObservables(objects.filter((object) => SCO_TYPES.has(object.type)));
    setTtps(filterTTPs(objects));
    setMitreAttacks(objects.filter(isMitreAttack));
  }, [objects]);

  const getJobUrl = () => {
    if (teamId) {
      return URLS.teamJobDetail(
        teamId || "",
        report?.stixify_file_metadata?.id || "",
        report?.stixify_job_metadata?.id || ""
      );
    } else {
      return URLS.staffJobDetail(
        report?.stixify_file_metadata?.id || "",
        report?.stixify_job_metadata?.id || ""
      );
    }
  };
  const getProfileUrl = () => {
    if(teamId) return URLS.teamStixifyProfileDetail(teamId, report?.profile_id)
    return URLS.staffProfileDetail(report?.profile_id || '')
  }
  return (
    <Box p={4}>
      <Box sx={{ display: "flex" }}>
        <Typography variant="h4" gutterBottom>
          {report?.stixify_file_metadata.name}
        </Typography>
        {teamId && (
          <Box>
            {activeTeam?.allowed_create_dossiers ? (
              <Button
                variant="contained"
                sx={{ marginLeft: "2rem" }}
                onClick={() => setOpenAddToDossierDialog(true)}
              >
                Add to dossier
              </Button>
            ) : (
              <Tooltip title="Upgrade your plan to be able to add a Dossier">
                <div>
                  <Button
                    variant="contained"
                    sx={{ marginLeft: "2rem" }}
                    disabled
                  >
                    Add to dossier
                  </Button>
                </div>
              </Tooltip>
            )}
          </Box>
        )}
        {teamId && (
          <Link to={URLS.teamDossiers(teamId, { report_id: report?.id })}>
            <Button variant="contained" sx={{ marginLeft: "2rem" }}>
              View report dossiers
            </Button>
          </Link>
        )}
        {report?.team_id === activeTeam?.id && (
          <Link to={getJobUrl()}>
            <Button variant="contained" sx={{ marginLeft: "2rem" }}>
              Report Job
            </Button>
          </Link>
        )}
      </Box>
      <Box>
        <Box sx={{ display: "flex", marginBottom: "1rem" }}>
          <Typography variant="h5"> Summary</Typography>
        </Box>

        <div>{summary || "Loading...."}</div>
      </Box>
      <Table sx={{ marginTop: "1rem", marginBottom: "2rem" }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold" }}>Report Setting</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Value</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>Report Date</TableCell>
            <TableCell>
              {getDateString(report?.stixify_file_metadata?.created)}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Report Author</TableCell>
            <TableCell>
              <Link to={URLS.teamReports(teamId, {team_id: report?.team_id})}>identity--{report?.team_id}</Link>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Report Label</TableCell>
            <TableCell>
              {report?.stixify_file_metadata?.labels?.join(", ")}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Report Confidence</TableCell>
            <TableCell>{report?.stixify_file_metadata?.confidence}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Report TLP</TableCell>
            <TableCell>{report?.stixify_file_metadata?.tlp_level}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Report ID</TableCell>
            <TableCell>report--{report?.id}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Profile ID</TableCell>
            <TableCell>
              <Link to={getProfileUrl()}>
                {report?.profile_name} ({report?.profile_id})
              </Link>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <Box sx={{ display: "flex", marginBottom: "1rem" }}>
        <Typography variant="h5"> Report Content</Typography>
      </Box>

      <div
        style={{
          border: "1px solid #000000cc",
          padding: "3rem",
          borderRadius: "5px",
          maxWidth: "80vw",
        }}
      >
        <SyntaxHighlighter
          showLineNumbers
          useInlineStyles
          wrapLines
          wrapLongLines
          language="text"
        >
          {markdown}
        </SyntaxHighlighter>
      </div>
      <div>
        <Box
          sx={{ display: "flex", marginTop: "1rem", justifyContent: "right" }}
        >
          <Button onClick={downloadOriginalFile} variant="contained">
            Download Original
          </Button>
          <Button
            sx={{ marginLeft: "2rem" }}
            onClick={downloadMarkdown}
            variant="contained"
          >
            Download Markdown
          </Button>
        </Box>
        {}
      </div>

      {loading ? (
        <CircularProgress style={{ display: "block", margin: "20px auto" }} />
      ) : (
        <>
          <ExtractionTables
            objects={filterObjects(objects)}
            getObservationSearchUrl={getObservationSearchUrl}
            removeObject={removeObject}
            showDelete={(teamId && report?.team_id === activeTeam?.id) || false}
          ></ExtractionTables>
        </>
      )}

      <Box sx={{ marginTop: "5rem" }}>
        <Typography variant="h5">Extraction Graph</Typography>
        <div ref={stixRef}></div>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          {!teamId || activeTeam?.allowed_data_download ? (
            <Button onClick={downloadStixObject} variant="contained">
              Download
            </Button>
          ) : (
            <Tooltip title="Your team plan must be upgraded to allow data downloads to download the bundle">
              <div>
                <Button variant="contained" disabled={true}>
                  Download
                </Button>
              </div>
            </Tooltip>
          )}
        </Box>
      </Box>
      <Box sx={{ marginTop: "5rem" }}>
        <Typography variant="h5">API Request</Typography>
        <Typography>
          <p>
            You can use the REST API to download the STIX bundle for this
            object.
          </p>
        </Typography>
        <SyntaxHighlighter language="bash" style={solarizedlight}>
          {CURL_OBJECTS_COMMAND.replace("reportId", reportId || "")}
        </SyntaxHighlighter>
        <Box>
          <Box>
            <Typography>
              <p>
                Replace <code className="confirm-text">XXXX</code> with your API
                key. You can generate this on your{" "}
                <Link target="_blank" to={URLS.profile()}>
                  {" "}
                  account settings page
                </Link>{" "}
                (only if your teams plan supports API access).
              </p>
            </Typography>
            <Typography>
              <p>
                You can do lots more with the APIs to integrate this data with
                your other tooling. Check the{" "}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={REACT_APP_API_SWAGGER_URL}
                >
                  REST API docs
                </a>{" "}
                or{" "}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={REACT_APP_TAXII_SWAGGER_URL}
                >
                  TAXII API docs
                </a>{" "}
                for more information.
              </p>
            </Typography>
          </Box>
        </Box>
      </Box>
      {activeTeam && id && (
        <AddToDossierDialog
          reportId={id}
          teamId={activeTeam.id}
          open={openAddToDossierDialog}
          onClose={() => setOpenAddToDossierDialog(false)}
        />
      )}
      {selectedObject && report && (
        <DeleteFromObjectDialog
          open={showConfirmDeleteFromObjectDialog}
          onClose={() => setShowConfirmDeleteFromObjectDialog(false)}
          onDelete={() => onDeleteFromSelectedObject()}
          object={selectedObject}
          reportId={reportId}
          report={report}
          teamId={activeTeam?.id || ""}
        ></DeleteFromObjectDialog>
      )}
    </Box>
  );
};

export default ReportExtractionPage;
